// Sentry initialization. Loaded once at app startup. Only activates when
// `VITE_SENTRY_DSN` is configured in the environment (Vercel project settings
// or .env.local), so developers running locally without a DSN see no
// network traffic / no Sentry SDK calls.
//
// The DSN is a public token (safe to ship to the browser) — Sentry rate-limits
// per project and you should restrict allowed origins in the Sentry dashboard.

let initialized = false;

export async function initSentry() {
  if (initialized) return;
  if (typeof window === "undefined") return;
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return; // Sentry not configured — no-op.

  try {
    const Sentry = await import("@sentry/react");
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE || "production",
      release: import.meta.env.VITE_APP_VERSION || undefined,
      // Tame default integrations — we don't need session replay (heavy) or
      // global handler unless explicitly asked for.
      integrations: [
        Sentry.browserTracingIntegration({ enableInp: true }),
      ],
      tracesSampleRate: 0.1,    // 10% perf sampling
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // Filter out the noisy lock-stolen rejections we already suppress, plus
      // benign network errors caused by users walking out of WiFi range.
      ignoreErrors: [
        /Lock.*was released because another request stole it/i,
        /AbortError/i,
        /NetworkError when attempting to fetch resource/i,
        /Load failed/i, // iOS Safari truncation when offline
      ],
    });
    initialized = true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[PettoBook] Sentry init failed:", e);
  }
}

// Convenience helper for non-React code paths (api.js, useAppState) to
// report an error without needing a static import of the SDK.
export async function captureError(err, context = {}) {
  try {
    const Sentry = await import("@sentry/react");
    Sentry.captureException(err, { extra: context });
  } catch { /* sentry not loaded — silent */ }
}
